import React, { useState, useEffect } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import ApiService from '../../../config/api';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import {
  CircularProgress,
  IconButton,
  Button,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Snackbar,
  Alert,
  useTheme,
  useMediaQuery
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import TitleIcon from '@mui/icons-material/Title';
import ImageIcon from '@mui/icons-material/Image';
import AdminNavbar from '../components/AdminNavbar';
import Sidebar from '../components/Sidebar';

const BlogForm = () => {
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [sidebarOpen, setSidebarOpen] = useState(!isSmallScreen);
  const toggleDrawer = () => setSidebarOpen(!sidebarOpen);

  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [description, setDescription] = useState('');
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [existingImage, setExistingImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEditMode);
  const [error, setError] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  const navigate = useNavigate();

  const handleTitleChange = (e) => {
    const value = e.target.value;
    setTitle(value);
    const generatedSlug = value
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .trim()
      .replace(/\s+/g, '-');
    setSlug(generatedSlug);
  };


  useEffect(() => {
    if (isEditMode) {
      const fetchBlog = async () => {
        try {
          const response = await ApiService.get(`/blogs/admin/read/${id}`);
          if (response.data.error) {
            throw new Error(response.data.message);
          }
          const blogData = response.data.meta;
          console.log(blogData)
          setTitle(blogData.title);
          setDescription(blogData.description);
          setSlug(blogData.slug)
          setExistingImage(blogData.image);
        } catch (err) {
          setError('Failed to fetch blog details.');
          console.error(err);
          showSnackbar('Failed to fetch blog details.', 'error');
        } finally {
          setFetching(false);
        }
      };
      fetchBlog();
    }
  }, [id, isEditMode]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    } else {
      setImage(null);
      setImagePreview(null);
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const formdata = new FormData();
      formdata.append('title', title);
      formdata.append('slug', slug);
      formdata.append('description', description);

      if (image) {
        formdata.append('image', image);
      }

      let response;

      if (isEditMode) {
        // Use update API for edit mode
        response = await ApiService.put(`blogs/edit/${id}`, formdata, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
      } else {
        // Use create API for new blog
        response = await ApiService.post('blogs/create-blog', formdata, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
      }

      showSnackbar(
        isEditMode ? 'Blog updated successfully!' : 'Blog created successfully!',
        'success'
      );

      setTimeout(() => {
        navigate('/admin/blogs');
      }, 2000);
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} blog:`, error);
      showSnackbar(
        `Failed to ${isEditMode ? 'update' : 'create'} blog. Please try again.`,
        'error'
      );
    } finally {
      setLoading(false);
    }
  };

  const quillModules = {
    toolbar: [
      [{ header: [1, 2, false] }],
      ['bold', 'italic', 'underline', 'strike', 'blockquote'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['link', 'image'],
      ['clean'],
    ],
  };

  const quillFormats = [
    'header',
    'bold',
    'italic',
    'underline',
    'strike',
    'blockquote',
    'list',
    'bullet',
    'link',
    'image',
  ];

  if (fetching) {
    return (
      <Box
        display="grid"
        gridTemplateColumns={sidebarOpen ? "200px 1fr" : "60px 1fr"}
        sx={{
          transition: "grid-template-columns 0.3s ease-in-out",
          backgroundColor: 'var(--bs-secondary)',
          minHeight: '100vh',
        }}
      >
        <Sidebar open={sidebarOpen} toggleDrawer={toggleDrawer} setOpen={setSidebarOpen} />
        <Box>
          <AdminNavbar />
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
            <CircularProgress sx={{ color: 'var(--bs-primary)' }} />
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      display="grid"
      gridTemplateColumns={sidebarOpen ? "200px 1fr" : "60px 1fr"}
      sx={{
        transition: "grid-template-columns 0.3s ease-in-out",
        backgroundColor: 'var(--bs-secondary)',
        minHeight: '100vh',
      }}
    >
      <Sidebar open={sidebarOpen} toggleDrawer={toggleDrawer} setOpen={setSidebarOpen} />
      <Box>
        <AdminNavbar />
        <div style={{ backgroundColor: 'var(--bs-secondary)', padding:10}}>
           <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
            sx={{
              color: "var(--bs-primary)",
              textTransform: "none",
              fontWeight: 500,
            }}
          >
            Back to Blogs
          </Button>
          <Grid container justifyContent="center">
            <Grid item xs={12} md={8}>
              <Card sx={{ backgroundColor: 'var(--bs-primary)', color: 'var(--bs-accent)' }}>
                <CardContent>
                  <Typography variant="h5" component="h3" gutterBottom align="center" sx={{ color: 'var(--bs-accent)' }}>
                    {isEditMode ? 'Edit Blog' : 'Create New Blog'}
                  </Typography>

                  <form onSubmit={handleSubmit}>
                    <Box sx={{ mb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <IconButton sx={{ color: 'var(--bs-accent)' }}>
                          <TitleIcon />
                        </IconButton>
                        <Typography variant="subtitle1" component="label" htmlFor="title-input" sx={{ color: 'var(--bs-accent)' }}>
                          Title
                        </Typography>
                      </Box>
                      <input
                        type="text"
                        id="title-input"
                        className="form-control"
                        value={title}
                        onChange={handleTitleChange}
                        required
                        style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ccc' }}
                      />
                    </Box>

                    <Box sx={{ mb: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Typography
                          variant="subtitle1"
                          component="label"
                          htmlFor="slug-input"
                          sx={{ color: 'var(--bs-accent)' }}
                        >
                          Slug
                        </Typography>
                      </Box>
                      <input
                        type="text"
                        id="slug-input"
                        className="form-control"
                        value={slug}
                        onChange={(e) => setSlug(e.target.value)}
                        required
                        style={{
                          width: '100%',
                          padding: '10px',
                          borderRadius: '4px',
                          border: '1px solid #ccc',
                        }}
                      />
                    </Box>


                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle1" component="label" htmlFor="description-editor" sx={{ mb: 1, color: 'var(--bs-accent)' }}>
                        Description
                      </Typography>
                      <ReactQuill
                        theme="snow"
                        value={description}
                        onChange={setDescription}
                        modules={quillModules}
                        formats={quillFormats}
                        required
                        style={{ color: 'var(--bs-accent)' }}
                      />
                    </Box>

                    <Box sx={{ mb: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <IconButton sx={{ color: 'var(--bs-accent)' }}>
                          <ImageIcon />
                        </IconButton>
                        <Typography variant="subtitle1" component="label" htmlFor="image-input" sx={{ color: 'var(--bs-accent)' }}>
                          Image
                        </Typography>
                      </Box>
                      <input
                        type="file"
                        id="image-input"
                        className="form-control"
                        onChange={handleImageChange}
                        accept="image/*"
                        required={!isEditMode} // Only required for new blogs
                        style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ccc' }}
                      />
                      {!image && isEditMode && existingImage && (
                        <Typography variant="body2" sx={{ mt: 1, color: 'var(--bs-accent)' }}>
                          Current image will be kept if no new image is selected.
                        </Typography>
                      )}
                    </Box>

                    {(imagePreview || (existingImage && !image)) && (
                      <Box sx={{ mb: 3, textAlign: 'center' }}>
                        <img
                          src={imagePreview || existingImage}
                          alt="Preview"
                          style={{ maxWidth: '100%', maxHeight: '300px', borderRadius: '8px', objectFit: 'contain' }}
                        />
                        <Button
                          variant="outlined"
                          onClick={() => {
                            setImage(null);
                            setImagePreview(null);
                          }}
                          sx={{ mt: 2, color: 'var(--bs-accent)', borderColor: 'var(--bs-accent)' }}
                        >
                          Remove Image
                        </Button>
                      </Box>
                    )}

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                      <Button
                        variant="outlined"
                        onClick={() => navigate('/admin/blogs')}
                        sx={{ color: 'var(--bs-accent)', borderColor: 'var(--bs-secondary)' }}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        variant="contained"
                        endIcon={loading ? null : <SendIcon />}
                        disabled={loading}
                        sx={{
                          backgroundColor: 'var(--bs-accent)',
                          color: 'var(--bs-primary)',
                          '&:hover': { backgroundColor: 'var(--bs-secondary)' },
                        }}                      >
                        {loading ? (
                          <CircularProgress size={24} color="inherit" />
                        ) : (
                          isEditMode ? 'Update Blog' : 'Submit Blog'
                        )}
                      </Button>
                    </Box>
                  </form>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </div>
      </Box>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BlogForm;